import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Eye,
  FileStack,
  Globe,
  Mail,
  PenSquare,
  Rocket,
  Send,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { useDashboard } from "@/components/Layout";
import { Empty, ErrorBlock, ItemStatusBadge, Loading } from "@/components/ui";
import { api } from "@/lib/api";
import { fmtDuration, type Trending } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { CONTENT_TYPES, fmtAgo, fmtDate, fmtNumber, ITEM_STATUS } from "@/lib/format";
import { ROLE_LABEL } from "@/lib/roles";
import type { Dashboard as DashboardData, ItemStatus } from "@/lib/types";

/*
 * The newsroom at a glance, in the order an editor asks the questions:
 *   1. Does anything need me?        -> "Needs attention" (only what is actionable)
 *   2. How did we do this week?      -> four outcome figures, each compared with last week
 *   3. Where is the content?         -> the pipeline, then what changed most recently
 *   4. What is the library made of, is the newsletter on track, is the machine healthy?
 * Each number appears once on the page.
 */

const typeLabel = (value: string) => CONTENT_TYPES.find((t) => t.value === value)?.label ?? value;
const today = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" });
const shortDay = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" });

function greeting() {
  const hour = new Date().getHours();
  return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
}

function total(data: DashboardData) {
  return Object.values(data.content.by_status).reduce((sum, n) => sum + n, 0);
}

export function Dashboard() {
  const { can } = useAuth();
  const { data, error, isLoading, refetch } = useDashboard();
  // Shares its cache with the Trending card on the Content page.
  const readers = useQuery({
    queryKey: ["analytics", "trending", 7],
    queryFn: () => api<Trending>("/analytics/trending/", { query: { days: 7 } }),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorBlock error={error} onRetry={refetch} />;

  const name = data.me.first_name || data.me.username;

  return (
    <>
      <section className="hero dash-hero">
        <div style={{ position: "relative", zIndex: 1 }}>
          <p className="hero__eyebrow">
            {ROLE_LABEL[data.me.role]} · {today.format(new Date())}
          </p>
          <h1>
            {greeting()}, {name}.
          </h1>
          <p>Here’s what needs you, and how the newsroom is doing this week.</p>
        </div>
        <div className="hero__actions">
          {can("editor") ? (
            <>
              <Link className="btn btn--primary" to="/content?status=waiting_review">
                <ClipboardCheck size={16} /> Open review queue
              </Link>
              <Link className="btn" to="/newsletter/new">
                <PenSquare size={16} /> New newsletter
              </Link>
            </>
          ) : (
            <Link className="btn btn--primary" to="/content">
              Browse content <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </section>

      <Attention data={data} />

      <section aria-labelledby="week-title" className="dash-week">
        <h2 id="week-title" className="dash-section-title">
          This week <span>last 7 days, compared with the 7 before</span>
        </h2>
        <div className="dash-kpis">
          <PublishedTile data={data} />
          <ReadersTile query={readers} to={can("super_admin") ? "/visitors" : "/content"} />
          <ReadTimeTile query={readers} />
          {data.newsletter ? (
            <Tile
              icon={<Mail size={16} />}
              label="Subscribers"
              value={fmtNumber(data.newsletter.subscribers.active)}
              to={can("admin") ? "/newsletter/subscribers" : "/newsletter"}
              foot={
                <>
                  <Delta now={data.newsletter.subscribers.new_7d} unit="new" plain />
                  <span>{fmtNumber(data.newsletter.subscribers.unsubscribed)} unsubscribed in total</span>
                </>
              }
            />
          ) : (
            <Tile
              icon={<FileStack size={16} />}
              label="Library"
              value={fmtNumber(data.content.by_status.published ?? 0)}
              to="/content?status=published"
              foot={<span>published stories in total</span>}
            />
          )}
        </div>
      </section>

      <div className="grid grid--main-side">
        <div className="stack stack--lg">
          <section className="card dash-panel">
            <PanelHead
              title="Content pipeline"
              sub={`${fmtNumber(total(data))} items, from found to published`}
              action={
                <Link className="dash-link" to="/content">
                  Open content <ArrowRight size={14} />
                </Link>
              }
            />
            <Pipeline data={data} />
          </section>

          <section className="card dash-panel">
            <PanelHead title="Recently updated" sub="The latest changes across all content" />
            {data.recent_items.length ? (
              <div className="dash-list">
                {data.recent_items.map((item) => (
                  <Link key={item.id} to={`/content/${item.id}`} className="dash-list__row">
                    <span className={`dash-type dash-type--${item.content_type}`} aria-hidden="true">
                      {typeLabel(item.content_type).slice(0, 1)}
                    </span>
                    <span className="dash-list__main">
                      <span className="dash-list__title">{item.heading || `Item #${item.id}`}</span>
                      <span className="dash-list__meta">
                        {typeLabel(item.content_type)} · #{item.id} · {fmtAgo(item.updated_at)}
                      </span>
                    </span>
                    <ItemStatusBadge status={item.status} />
                    <ChevronRight size={16} className="dash-list__go" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            ) : (
              <Empty title="Nothing here yet">Items appear as the agents find them.</Empty>
            )}
          </section>

          <section className="card dash-panel">
            <PanelHead title="Your recent activity" sub="What you changed, newest first" />
            {data.my_activity.length ? (
              <ol className="dash-activity">
                {data.my_activity.map((entry) => (
                  <li key={entry.id}>
                    <span className="dash-activity__dot" aria-hidden="true" />
                    <span className="dash-activity__main">
                      <span className="dash-activity__what">
                        {entry.to_status ? `Moved to ${ITEM_STATUS[entry.to_status as ItemStatus]?.label ?? entry.to_status}` : entry.message || entry.action}
                      </span>
                      <span className="dash-activity__meta">
                        {entry.item_id ? <Link to={`/content/${entry.item_id}`}>{entry.item_heading || `Item #${entry.item_id}`}</Link> : null}
                        {entry.item_id ? " · " : ""}
                        {fmtAgo(entry.created_at)}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="dash-panel__empty">
                {data.me.role === "viewer" ? "Viewers read, so there’s no activity of yours yet." : "Your edits and reviews will show here."}
              </p>
            )}
          </section>
        </div>

        <div className="stack stack--lg">
          {data.review_queue && data.review_queue.length > 0 && (
            <section className="card dash-panel">
              <PanelHead
                title="Waiting longest"
                sub="Oldest first: review these before newer stories"
                action={
                  <Link className="dash-link" to="/content?status=waiting_review">
                    Review <ArrowRight size={14} />
                  </Link>
                }
              />
              <div className="dash-list">
                {data.review_queue.map((item) => (
                  <Link key={item.id} to={`/content/${item.id}`} className="dash-list__row dash-list__row--queue">
                    <span className="dash-list__main">
                      <span className="dash-list__title">{item.heading || `Item #${item.id}`}</span>
                      <span className="dash-list__meta">
                        <Clock3 size={12} aria-hidden="true" /> waiting {fmtAgo(item.created_at).replace(/ ago$/, "")}
                      </span>
                    </span>
                    <ChevronRight size={16} className="dash-list__go" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          <ContentMix data={data} />

          {data.newsletter && (
            <section className="card dash-panel">
              <PanelHead
                title="Newsletter"
                sub="The Dispatch"
                action={
                  <Link className="dash-link" to="/newsletter">
                    Issues <ArrowRight size={14} />
                  </Link>
                }
              />
              <div className="dash-news">
                <div className="dash-news__last">
                  <span className="dash-news__icon">
                    <Send size={15} />
                  </span>
                  {data.newsletter.last_sent ? (
                    <Link to={`/newsletter/${data.newsletter.last_sent.id}`} className="dash-news__sent">
                      <small>Last sent {fmtDate(data.newsletter.last_sent.sent_at)}</small>
                      <span>{data.newsletter.last_sent.subject}</span>
                    </Link>
                  ) : (
                    <span className="dash-news__sent">
                      <small>Last sent</small>
                      <span>Nothing sent yet</span>
                    </span>
                  )}
                </div>
                <dl className="dash-news__stats">
                  <div>
                    <dt>Drafts</dt>
                    <dd>{fmtNumber(data.newsletter.issues.draft)}</dd>
                  </div>
                  <div>
                    <dt>Scheduled</dt>
                    <dd>{fmtNumber(data.newsletter.issues.scheduled)}</dd>
                  </div>
                  <div>
                    <dt>New, 30 days</dt>
                    <dd>{fmtNumber(data.newsletter.subscribers.new_30d)}</dd>
                  </div>
                </dl>
                {can("editor") && (
                  <Link className="btn btn--primary dash-news__cta" to="/newsletter/new">
                    <Sparkles size={15} /> Write an issue
                  </Link>
                )}
              </div>
            </section>
          )}

          {data.sources && data.agents && <SystemHealth data={data} />}

        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ pieces */

function PanelHead({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <header className="dash-panel__head">
      <div>
        <h2 className="dash-panel__title">{title}</h2>
        {sub && <p className="dash-panel__sub">{sub}</p>}
      </div>
      {action}
    </header>
  );
}

/** Only what someone can act on. When nothing needs attention, one calm line says so. */
function Attention({ data }: { data: DashboardData }) {
  const oldest = data.review_queue?.[0];
  const items: { tone: "warn" | "bad" | "info"; icon: ReactNode; title: string; text: string; to: string }[] = [];
  if (data.content.waiting_review) {
    items.push({
      tone: "warn",
      icon: <ClipboardCheck size={18} />,
      title: `${fmtNumber(data.content.waiting_review)} waiting for review`,
      text: oldest ? `Oldest has waited ${fmtAgo(oldest.created_at).replace(/ ago$/, "")}` : "Checked by the agents, ready for an editor",
      to: "/content?status=waiting_review",
    });
  }
  if (data.content.failed) {
    items.push({
      tone: "bad",
      icon: <AlertTriangle size={18} />,
      title: `${fmtNumber(data.content.failed)} failed ${data.content.failed === 1 ? "item" : "items"}`,
      text: "Each shows its reason and can be retried",
      to: "/content?status=failed",
    });
  }
  if (data.sources?.failing) {
    items.push({
      tone: "bad",
      icon: <Globe size={18} />,
      title: `${data.sources.failing} source ${data.sources.failing === 1 ? "website is" : "websites are"} failing`,
      text: "The last check could not read the feed",
      to: "/sources",
    });
  }
  if (data.agents?.errors_24h) {
    items.push({
      tone: "bad",
      icon: <Bot size={18} />,
      title: `${data.agents.errors_24h} agent ${data.agents.errors_24h === 1 ? "error" : "errors"} in 24 hours`,
      text: "See what went wrong in the activity log",
      to: "/activity",
    });
  }
  if (data.imports?.ready_to_confirm) {
    items.push({
      tone: "info",
      icon: <Upload size={18} />,
      title: `${data.imports.ready_to_confirm} ${data.imports.ready_to_confirm === 1 ? "import is" : "imports are"} ready to confirm`,
      text: "Checked files wait for your go-ahead",
      to: "/imports",
    });
  }

  return (
    <section aria-labelledby="attention-title" className="dash-attn">
      <h2 id="attention-title" className="dash-section-title">
        Needs attention {items.length > 0 && <em>{items.length}</em>}
      </h2>
      {items.length ? (
        <div className="dash-attn__grid">
          {items.map((item) => (
            <Link key={item.title} to={item.to} className={`dash-attn__item dash-attn__item--${item.tone}`}>
              <span className="dash-attn__icon">{item.icon}</span>
              <span className="dash-attn__text">
                <strong>{item.title}</strong>
                <small>{item.text}</small>
              </span>
              <ArrowRight size={16} className="dash-attn__go" aria-hidden="true" />
            </Link>
          ))}
        </div>
      ) : (
        <div className="dash-attn__clear">
          <CheckCircle2 size={18} /> All clear. Nothing is waiting for review, failing or broken.
        </div>
      )}
    </section>
  );
}

/** "+3 vs last week" / "−2 vs last week", or a plain count when there is nothing to compare. */
function Delta({ now, before, unit, plain }: { now: number; before?: number | null; unit?: string; plain?: boolean }) {
  if (plain) {
    return <span className={`dash-delta ${now > 0 ? "is-up" : ""}`}>+{fmtNumber(now)} {unit}</span>;
  }
  if (before === null || before === undefined) return null;
  const diff = now - before;
  if (diff === 0) return <span className="dash-delta">Same as last week</span>;
  const up = diff > 0;
  const pct = before ? ` (${Math.round((Math.abs(diff) / before) * 100)}%)` : "";
  return (
    <span className={`dash-delta ${up ? "is-up" : "is-down"}`}>
      {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
      {up ? "+" : "−"}
      {fmtNumber(Math.abs(diff))}
      {pct} vs last week
    </span>
  );
}

function Tile({
  icon,
  label,
  value,
  foot,
  to,
  feature,
  children,
  extra,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  to?: string;
  feature?: boolean;
  children?: ReactNode;
  extra?: ReactNode;
}) {
  const body = (
    <>
      <span className="dash-kpi__top">
        <span className="dash-kpi__icon">{icon}</span>
        <span className="dash-kpi__label">{label}</span>
        {to && <ArrowUpRight size={16} className="dash-kpi__go" aria-hidden="true" />}
      </span>
      <span className="dash-kpi__value">{value}</span>
      {children ?? extra}
      {foot && <span className="dash-kpi__hint">{foot}</span>}
    </>
  );
  const className = `dash-kpi ${feature ? "dash-kpi--feature" : "dash-kpi--calm"}`;
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

/** This week's output with a 14-day rhythm: last week's bars faded, this week's lit. */
function PublishedTile({ data }: { data: DashboardData }) {
  const days = data.content.published_daily;
  const max = Math.max(...days.map((d) => d.count), 1);
  return (
    <Tile
      feature
      icon={<Rocket size={16} />}
      label="Published"
      value={fmtNumber(data.content.published_7d)}
      to="/content?status=published"
      foot={<Delta now={data.content.published_7d} before={data.content.published_prev_7d} />}
    >
      <span className="dash-rhythm" role="img" aria-label="Stories published on each of the last 14 days">
        {days.map((d, i) => (
          <span
            key={d.day}
            className={`dash-rhythm__bar ${i >= days.length - 7 ? "is-this-week" : ""} ${d.count ? "" : "is-zero"}`}
            style={d.count ? { height: `${Math.max(14, (d.count / max) * 100)}%` } : undefined}
            title={`${shortDay.format(new Date(`${d.day}T00:00:00`))}: ${d.count} published`}
          />
        ))}
      </span>
    </Tile>
  );
}

type TrendingQuery = { data?: Trending; isLoading: boolean; error: unknown };

/** A light strip of daily bars for the white tiles (the latest day lit). */
function MiniBars({ values, label }: { values: { day: string; n: number }[]; label: string }) {
  const max = Math.max(...values.map((v) => v.n), 1);
  return (
    <span className="dash-mini" role="img" aria-label={label}>
      {values.map((v, i) => (
        <span
          key={v.day}
          className={`dash-mini__bar ${i === values.length - 1 ? "is-last" : ""} ${v.n ? "" : "is-zero"}`}
          style={v.n ? { height: `${Math.max(14, (v.n / max) * 100)}%` } : undefined}
          title={`${shortDay.format(new Date(`${v.day}T00:00:00`))}: ${v.n}`}
        />
      ))}
    </span>
  );
}

function ReadersTile({ query, to }: { query: TrendingQuery; to: string }) {
  const d = query.data;
  return (
    <Tile
      icon={<Users size={16} />}
      label="Readers"
      value={d ? fmtNumber(d.readers) : query.isLoading ? "…" : "—"}
      to={to}
      extra={d && <MiniBars values={d.daily.map((x) => ({ day: x.day, n: x.readers }))} label="Readers on each of the last 7 days" />}
      foot={
        d ? (
          d.previous_readers ? (
            <Delta now={d.readers} before={d.previous_readers} />
          ) : (
            <span>unique people on the public site</span>
          )
        ) : query.error ? (
          <span>Reader numbers are unavailable</span>
        ) : undefined
      }
    />
  );
}

function ReadTimeTile({ query }: { query: TrendingQuery }) {
  const d = query.data;
  return (
    <Tile
      icon={<Eye size={16} />}
      label="Avg. reading time"
      value={d ? fmtDuration(d.avg_read_seconds) : query.isLoading ? "…" : "—"}
      to="/content"
      extra={d && <MiniBars values={d.daily.map((x) => ({ day: x.day, n: x.opens }))} label="Page opens on each of the last 7 days" />}
      foot={d ? <span>per page, across {fmtNumber(d.opens)} page opens</span> : undefined}
    />
  );
}

/* The main path, in order; rejected, duplicates and failures are set aside from it. */
const FLOW: ItemStatus[] = ["found", "scraped", "checked", "waiting_review", "approved", "published"];
const FLOW_LABEL: Partial<Record<ItemStatus, string>> = { waiting_review: "Review" };

function Pipeline({ data }: { data: DashboardData }) {
  const count = (status: ItemStatus) => data.content.by_status[status] ?? 0;
  const all = Math.max(1, total(data));
  const segments = [...FLOW, "duplicate", "rejected", "failed"] as ItemStatus[];
  const aside = (["rejected", "duplicate"] as ItemStatus[]).filter((s) => count(s) > 0);
  return (
    <div className="dash-flow">
      <div className="dash-flow__bar" role="img" aria-label="Share of items in each stage">
        {segments
          .filter((status) => count(status) > 0)
          .map((status) => (
            <span
              key={status}
              className={`dash-flow__seg dash-flow__seg--${status}`}
              style={{ flexGrow: count(status) }}
              title={`${ITEM_STATUS[status].label}: ${count(status)} (${Math.round((count(status) / all) * 100)}%)`}
            />
          ))}
      </div>

      <ol className="dash-flow__steps">
        {FLOW.map((status, i) => (
          <li key={status} className={status === "published" ? "is-final" : count(status) ? "is-busy" : ""}>
            <Link to={`/content?status=${status}`} className="dash-step">
              <span className="dash-step__num">{fmtNumber(count(status))}</span>
              <span className="dash-step__label">
                <span className={`dash-dot dash-dot--${status}`} aria-hidden="true" />
                {FLOW_LABEL[status] ?? ITEM_STATUS[status].label}
              </span>
            </Link>
            {i < FLOW.length - 1 && <ChevronRight size={16} className="dash-step__arrow" aria-hidden="true" />}
          </li>
        ))}
      </ol>

      {aside.length > 0 && (
        <p className="dash-flow__aside">
          Set aside:{" "}
          {aside.map((status, i) => (
            <span key={status}>
              {i > 0 && " · "}
              <Link to={`/content?status=${status}`}>
                {fmtNumber(count(status))} {ITEM_STATUS[status].label.toLowerCase()}
              </Link>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

/** What the published library is made of. */
function ContentMix({ data }: { data: DashboardData }) {
  const rows = Object.entries(data.content.published_by_type)
    .filter(([, n]) => (n ?? 0) > 0)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0)) as [string, number][];
  const sum = rows.reduce((s, [, n]) => s + n, 0);
  return (
    <section className="card dash-panel">
      <PanelHead title="Content mix" sub={`${fmtNumber(sum)} published stories by type`} />
      {rows.length ? (
        <div className="dash-mix">
          <div className="dash-mix__bar" role="img" aria-label="Share of published stories by type">
            {rows.map(([type, n]) => (
              <span key={type} className={`dash-mix__seg dash-mix__seg--${type}`} style={{ flexGrow: n }} />
            ))}
          </div>
          <ul className="dash-mix__list">
            {rows.map(([type, n]) => (
              <li key={type}>
                <span className={`dash-mix__key dash-mix__seg--${type}`} aria-hidden="true" />
                <Link to={`/content?status=published&content_type=${type}`}>{typeLabel(type)}</Link>
                <strong>{fmtNumber(n)}</strong>
                <span>{Math.round((n / Math.max(1, sum)) * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="dash-panel__empty">Nothing published yet.</p>
      )}
    </section>
  );
}

/** Sources, agents, imports and the team in one place, each with a status light. */
function SystemHealth({ data }: { data: DashboardData }) {
  const rows: { icon: ReactNode; label: string; detail: string; ok: boolean; to: string }[] = [];
  if (data.sources) {
    rows.push({
      icon: <Globe size={15} />,
      label: "Sources",
      detail: `${data.sources.active} of ${data.sources.total} active${data.sources.failing ? ` · ${data.sources.failing} failing` : " · all reading"}`,
      ok: !data.sources.failing,
      to: "/sources",
    });
  }
  if (data.agents) {
    rows.push({
      icon: <Bot size={15} />,
      label: "Agents",
      detail: data.agents.errors_24h ? `${data.agents.errors_24h} errors in the last 24 hours` : "No errors in the last 24 hours",
      ok: !data.agents.errors_24h,
      to: "/agents",
    });
  }
  if (data.imports) {
    rows.push({
      icon: <Upload size={15} />,
      label: "Imports",
      detail: data.imports.failed_7d ? `${data.imports.failed_7d} failed this week` : "No failed imports this week",
      ok: !data.imports.failed_7d,
      to: "/imports",
    });
  }
  if (data.users) {
    const roles = Object.entries(data.users.by_role)
      .filter(([, n]) => n > 0)
      .map(([role, n]) => `${n} ${ROLE_LABEL[role as keyof typeof ROLE_LABEL] ?? role}`)
      .join(", ");
    rows.push({
      icon: <Users size={15} />,
      label: "Team",
      detail: `${roles}${data.users.inactive ? ` · ${data.users.inactive} deactivated` : ""}`,
      ok: true,
      to: "/users",
    });
  }
  const healthy = rows.every((r) => r.ok);
  return (
    <section className="card dash-panel">
      <PanelHead title="System health" sub={healthy ? "Everything is running normally" : "Something needs a look"} />
      <ul className="dash-health">
        {rows.map((row) => (
          <li key={row.label}>
            <Link to={row.to} className="dash-health__row">
              <span className={`dash-health__icon ${row.ok ? "" : "is-bad"}`}>{row.icon}</span>
              <span className="dash-health__main">
                <strong>{row.label}</strong>
                <small>{row.detail}</small>
              </span>
              <span className={`dash-health__light ${row.ok ? "is-ok" : "is-bad"}`} aria-label={row.ok ? "OK" : "Needs a look"} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
